import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { api } from '@/api'
import type { AssetItem, JobError, JobOutput, JobState, ServerJobItem, WorkItem } from '@/api/types'
import { ESTIMATED_MS, progressFrom } from '@/config/catalog'
import { familyById, slugForFamily } from '@/config/modelFamilies'
import { canonPageKey } from '@/config/pageKeys'
import { FIRST_SYNC_MS, IDLE_POLL_MS, PROGRESS_TICK_MS, STATE_POLL_MS } from '@/config/polling'
import { useAssetLibrary } from './AssetLibraryContext'
import { useAuth } from './AuthContext'
import { useToast } from './ToastContext'
import { useWorks } from './WorksContext'
import { useModels } from './ModelsContext'

/**
 * `app_slug` 过滤判据 —— 单值或**一组**都认（不传 = 不过滤）。
 *
 * 三个取数入口（`jobsFor` / `outputsFor` / `refCandidatesFor`）共用这一份 ——
 * 各写一遍就是本项目反复复发的「同一份规则两处各写」。
 *
 * 为什么后来要接受数组：视频合并页 2026-10-05 把「带货短视频 / 商品视频」并成了
 * 一档，但库里 `main-video` 那批历史记录还在。单值过滤会把它们藏起来 ——
 * 那不是「正常收窄」，是真的少了一批用户自己的东西。
 */
function slugHit(jobSlug: string, filter?: string | string[]): boolean {
  if (!filter) return true
  return Array.isArray(filter) ? filter.includes(jobSlug) : jobSlug === filter
}

export type Job = {
  id: string
  serverJobId?: string
  title: string
  appSlug: string
  sourcePage: string
  /** 实际提交给后端的路由 slug */
  model: string
  /** 用户选的模型显示名（如「GPT Image 2.5」），展示用 */
  modelName?: string
  /** 用户选的模型族 id，重试时用它重新解析 slug */
  familyId?: string
  prompt: string
  /** 逐张提示词（套图类任务按张拆解，见 api/types.ts 的说明） */
  prompts?: string[]
  /**
   * 逐张尺寸（与 prompts 一一对应）。存下来是为了**重试时不再整批压成一个比例** ——
   * A+ 各模块官方尺寸不同，重试丢了它就等于重试出一版错版图。
   */
  ratios?: string[]
  inputs: Record<string, unknown>
  imageUrls: string[]
  /**
   * 参考视频地址。与 `imageUrls` 分开存 —— 重试时要原样带回去，
   * 拼在一起的话重试会把视频当图片重发，出的片子跟原任务不是一回事。
   */
  videoUrls?: string[]
  aspectRatio: string
  /** 分辨率档位。以前没存，导致「重试」只能退回默认档 —— 花一样的钱出不一样的图 */
  resolution?: string
  expectedOutputs: number
  estimatedMs: number
  state: JobState
  progress: number
  outputs: JobOutput[]
  error?: JobError
  coins?: number
  cost?: number
  createdAt: number
  finishedAt?: number
  /**
   * 投放平台档案 id —— 重试时要原样带回去，否则重试出来的图和原任务不是一个语境
   * （比如原任务是 Amazon 英文、重试变成默认中文）。
   */
  platform?: string
  /**
   * 批次 id。一次「批量表格」提交产生的 **N 个 job 共用它** —— 右栏据此聚成
   * 一张批次卡（而不是 20 张互不相干的卡）、任务中心据此还原「这是一批」。
   *
   * 空 / 不传 = 单个提交。**判空不能省**：老库补列后的历史行 batchId 全是空串，
   * 把空串当批次 id 的话，所有单个任务会被聚成一张假批次卡。
   *
   * ⚠️ 这一层只存 id，**批次的汇总数字（N 行 / 进行中几行）一律派生**
   * （按 batchId 数一遍），不存 —— 存了就会和 job 列表的真实状态打架。
   */
  batchId?: string
  /**
   * `true` = 这条是从**后端任务表**读回来的历史记录，浏览器没有提交过它。
   *
   * 为什么要区分：
   * 1. 它不进 localStorage —— 数据本来就在库里，存一份到浏览器只会撑爆配额，
   *    还会在下次启动时和接口回来的那份打架；
   * 2. 它不进生成页右栏的「本轮结果」—— 那是**本机本轮**的实时面板，
   *    把几十条历史产出倒进去就不是「本轮」了。历史归任务中心 / 我的作品。
   */
  remote?: boolean
  /**
   * 已被用户「清理」（任务卡上的「移除记录」/ 任务中心的「清空已结束」）。
   *
   * ⚠️ 右栏结果卡那颗「清空」**不置这一位**（它只收起视图，见 `clearResults`）。
   *
   * ⚠️ 它**不是删除**：后端只把 `hidden` 置 1，行原样留在库里、可恢复。
   * 这一位必须在库里而不是浏览器里 —— 上一版把「删过哪些」存在
   * localStorage（`hw_job_hidden_v1`），那是**第二份真源**：清缓存后视图状态没了，
   * 被移除的记录又全冒出来，用户看到的是「一页空白，过一会儿自己变回来」，
   * 自然得出「数据只在前端」的结论（2026-09-29 用户报障原话）。
   *
   * 默认列表里不会出现 `hidden` 的条目：已清理的那些走独立的 `hiddenJobs`，
   * 只在任务中心的「已清理」视图里露面。
   */
  hidden?: boolean
}

export type SubmitArgs = {
  appSlug: string
  title: string
  sourcePage: string
  model: string
  prompt: string
  /** 逐张提示词：一次 N 张时第 i 张用 prompts[i]，缺位回退到 prompt */
  prompts?: string[]
  /** 逐张尺寸：第 i 张用 ratios[i]（比例或成品像素），缺位回退 aspectRatio */
  ratios?: string[]
  inputs: Record<string, unknown>
  /**
   * **「真正被当成输入的那一格」在 `inputs` 里的键名**（有值 = 改提示词要连它一起改）。
   *
   * 🔴 2026-10-09 加。它堵的是一类「花钱买了错东西」的静默错：
   * 报价确认弹窗里的提示词是**可编辑**的（`PriceConfirmDialog` 那句
   * 「查看 / 编辑…」），改完由 `lib/promptEdits.withPromptEdits` 并回载荷。
   * 而有些档的**真输入不在 `prompt` 上**，在 `inputs` 的某一格：
   *   · 数字人 —— 会被念出来的是 `inputs.script`（后端 `_build_options` 读它），
   *     而 `prompt` 只是给人看的模板（上游一个字都不读）。**目前只有这一档声明它。**
   * 不声明这个键的话：用户在弹窗里把台词改了两句、确认，改的那一版**只落进
   * `prompt` 这一栏**，送到 TTS 的还是原来那段 —— 任务 succeeded、钱照扣、
   * 成片念的是旧台词，而界面上、任务记录里全是新台词（只有听成片才发现）。
   *
   * ⚠️ 只有一个键、不是「整份 inputs 替换」：`inputs` 里装着二十来个字段
   * （模式 / 音色 / 比例 / 素材地址…），整份换掉等于让弹窗顺带改了它们。
   * 没声明这个键的档**原样保留 `inputs` 的引用**（见 `withPromptEdits` 里那个
   * `inputs === base.inputs` 的用例）—— 不声明就不受影响，所以它是纯增量的。
   */
  promptInputKey?: string
  imageUrls?: string[]
  /**
   * **参考视频**。与 `imageUrls` 并列，不是二选一 ——
   * 参考图控形象、参考视频控运镜与节奏，上游多数视频模型两者都收。
   * 拼进 `imageUrls` 的话后端会按图片处理，视频会挂错到 `input_images` 上。
   */
  videoUrls?: string[]
  aspectRatio?: string
  resolution?: string
  outputs?: number
  estimatedMs?: number
  /** 投放平台档案 id —— 后端落库，用于复盘图内文字语言 / 比例从哪来 */
  platform?: string
  /**
   * **批次 id**。批量表格一次提交会调 N 次 `submit`（一行产品一次），
   * 由 `submitBatch` 统一生成同一个 id 塞进来 —— 调用方（生成页）**不需要知道它**。
   *
   * 单个提交不传 = 不分组。
   */
  batchId?: string
  /**
   * **skill 加载器**（第二道开关，默认不传 = 关）。
   *
   * 打开后文案任务会走「模型先读技能文档再动笔」的工具循环。后端环境
   * （`HW_SKILLS_ENABLE`）没开时传 true 会被 400 挡掉，所以发起方必须
   * 先确认 `useModels().skillsEnabled` —— 别无条件传。
   */
  skills?: boolean
}

type JobCenterApi = {
  jobs: Job[]
  activeJobs: Job[]
  activeCount: number
  submit: (args: SubmitArgs) => Promise<string>
  /**
   * **批量提交**：一次提交 N 个任务（一行产品一个），它们共用同一个批次 id，
   * 右栏聚成一张批次卡。
   *
   * 三条刻意的选择：
   * ① **串行**而不是 `Promise.all` —— 上游网关对并发提交有限流，N 行一起打过去
   *    容易被判成刷单；串行也天然给出「第几行」的进度。每个 job 本身是
   *    「立即返回 jobId、后台跑」，所以串行只串**提交**这一步（毫秒级），不串出图。
   * ② **一个一个来、失败不连坐** —— 第 3 行失败（参考图挂了 / 该行触发风控）
   *    不该让另外 19 行一起废掉。`submit` 内部已经把错误吃进 `state: 'failed'`，
   *    不抛出去，所以这里的循环不会断。
   * ③ **批次 id 由这里生成**，调用方不用管 —— 生成页只负责说「这是哪几行」，
   *    不该同时操心「它们怎么被重新聚起来」。
   *
   * 返回顺序与入参一一对应（失败的那一项返回的仍是它的本地 id，状态是 failed）。
   */
  submitBatch: (items: SubmitArgs[]) => Promise<string[]>
  retry: (jobId: string) => Promise<string | null>
  cancel: (jobId: string) => void
  dismiss: (jobId: string) => void
  clearFinished: () => void
  /**
   * 收起「结果」栏的一批产出（生成页结果卡上那颗「清空」）—— **只动这一栏**。
   *
   * ⚠️ 它**不碰库**、也**不从历史里移除记录**。这两个动作以前是同一份实现
   * （都走 `hideRecords`），于是用户在**结果卡**上点「清空」，右栏「本页历史」里的
   * 记录也跟着消失了 —— 而那颗按钮长在结果卡上、数字还是产出张数，读起来
   * 就是「清掉这一栏」（2026-09-30 报障原话：「为什么点击清空了就没了，
   * 我明明清空是结果」）。
   *
   * 记录级的清理请走 `dismiss`（任务卡上的「移除记录」）与 `clearFinished`
   * （任务中心的「清空已结束」）—— 那两颗才该动 `hidden`。
   *
   * **收的是 id 列表，不是页面键**：由 `StudioPanel` 用**正在显示的那批** job
   * （`isResultJob`）推导，调用方只传 `clearResults` 本身。若收页面键，调用方就得把
   * `jobsFor(X)` 的 X 再写一遍，那正是「读键写键不同源」事故的翻版（见 MEMORY 铁律）。
   */
  clearResults: (ids: string[]) => void
  /**
   * 被「清空结果」收起过的任务 id（同一份视图状态，只读）。
   *
   * 右栏靠它把**空态说准**：结果栏空着，但如果空的原因是「刚被收起」，
   * 那句「还没有结果」就是假的 —— 用户会读成「产物被删了」，
   * 而这正是 2026-09-30 那轮报障的起点。
   */
  clearedResultIds: Set<string>
  /**
   * 本页任务。第二个参数 `appSlug` 可选，传了就把范围收窄到**指定的工作流**。
   *
   * 见实现里的注释：一个页面有多个工作流时（商品图 8 档 / 详情页 5 档），
   * 它们的 `sourcePage` 相同、`app_slug` 不同，不收窄就串档。
   *
   * 2026-10-05 起第二参数也接受**数组**：视频合并页把「带货短视频 / 商品视频」
   * 并成了一档，但库里两边的历史都得看得见 —— 单值过滤会把 `main-video`
   * 那批记录藏起来。数组 = 「这些 slug 都算本页的」。
   */
  jobsFor: (sourcePage: string, appSlug?: string | string[]) => Job[]
  outputsFor: (sourcePage: string, appSlug?: string | string[]) => JobOutput[]
  /**
   * 候选参考图：某个工作流**全部**产出的图片地址，新 → 旧、去重。
   *
   * ⚠️ 和 `outputsFor` 的唯一区别就是**这里刻意保留 `remote`**，别顺手统一掉：
   * `outputsFor` 服务的是右栏「本轮结果」（本机本轮的实时面板，标题写着「本轮」）；
   * 而这里服务的是「挑一张当参考图」—— 用户上一轮跑出来的 AI 模特图，
   * 刷新页面后还在库里躺着，**挑不到才是 bug**（场景图的参考图正来自它）。
   *
   * 取 `type === 'image'`：视频/文本产出当不了图片参考图，
   * 混进候选条会让用户点了没反应（或传上去变成废地址）。
   */
  refCandidatesFor: (sourcePage: string, appSlug?: string | string[]) => string[]
  /** 后端任务历史是否已读到（false 时任务中心可能只是「还没拉回来」，不是「没有任务」） */
  historyLoaded: boolean
  /**
   * 上次读后端历史**失败**的原因（null = 成功）。
   *
   * 有它之前，「后端没响应」和「一条记录都没有」在界面上长得一模一样：
   * 生成页右栏那张历史卡会照着空态文案说「这里还没有跑过任务」——
   * 而库里可能躺着几十条。用户据此认为数据只在前端，是**这个谎**造成的。
   * 所以读取状态必须能被界面区分：读取中 / 读失败 / 真的没有。
   */
  historyError: string | null
  /** 手动重读后端任务历史（任务中心的「刷新记录」按钮） */
  refreshHistory: () => Promise<void>
  /**
   * 已清理的记录（后端 `hidden=only`）。
   *
   * 与 `jobs` **分开存**，理由同 `outputsFor` 排除 remote：
   * 把已清理的倒进主列表，右栏「本页历史」与任务中心默认视图会立刻重新变挤 ——
   * 那正是用户点「清空」想解决的事。
   */
  hiddenJobs: Job[]
  /** 库里已清理的总条数（后端下发，与分页无关）—— 「已清理 N 条」入口上的数字 */
  hiddenCount: number
  /** 恢复一批被清理的记录（复原成可见） */
  restoreJobs: (jobIds: string[]) => Promise<number>
}

const Ctx = createContext<JobCenterApi | null>(null)
const HISTORY_KEY = 'hw_job_history_v1'
/**
 * **已废弃**：老版本把「被用户移除过的记录 id」存在这里（浏览器黑名单）。
 *
 * 现在这份状态落在后端（`jobs.hidden`，见 `api.hideJobs`），理由：
 * 它是**用户意图**，天然属于服务端；存浏览器就是第二份真源 ——
 * 清缓存后视图状态丢了、记录又冒出来，用户以为数据只在前端。
 *
 * 这个常量只用于**一次性迁移**（把老黑名单搬进库，然后删掉本地键），
 * 迁移完就再也读不到它了。⚠️ 任何时候都不许再往它写。
 */
const LEGACY_HIDDEN_KEY = 'hw_job_hidden_v1'
/**
 * 「结果」栏被**收起**过的任务 id。
 *
 * ⚠️ 这是**视图状态**，不是记录状态 —— 它与「已清理」（`jobs.hidden`，落库）是两回事：
 *   · 收起结果（这个键）＝ 右栏那一栏的产出预览先收起来。库里一行不动，
 *     历史卡里的记录照旧在、点开照旧能看到产出。
 *   · 清理记录（`hideRecords`）＝ 把记录标成已清理，去「任务中心 → 已清理」恢复。
 *
 * 为什么它可以（也应该）落浏览器，而记录状态必须落库：结果栏的数据源
 * （`outputsFor`）本来就只吃**本机**任务 —— 它是一份本机视图，不是库里的账。
 * 记录状态是**用户意图**，存浏览器就成了第二份真源（清缓存后记录集体复活，
 * 用户以为「清空没生效」）—— 那是 2026-09-29 报障的病根，别再犯。
 *
 * 不落这个键的话，刷新一下产出全回来，用户会当成「清空没生效」。
 */
const RESULT_CLEARED_KEY = 'hw_result_cleared_v1'
/**
 * 这份名单最多留多少条。
 *
 * 它只可能装本机任务的 id（结果栏只吃本机任务，而本机任务本身只留最近 60 条），
 * 所以正常永远到不了这个数；留着是防「有人把窗口调大」之后无限长。
 */
const RESULT_CLEARED_CAP = 300
/** 服务端历史一次拉多少条；本地会话任务另有 60 条 localStorage 上限 */
const REMOTE_CAP = 150

/**
 * 「结果」栏吃哪几条任务 —— **唯一判据**，`outputsFor` 与右栏那颗「清空」共用。
 *
 * 两个条件都不是随手写的：
 *   · `!remote` —— 结果栏是**本机本轮**的实时面板（库里读回来的历史不进这里，
 *     库里有几十条，倒进来就不是「本轮」了）；
 *   · 有产出 —— 没有产出的任务是「历史」，不是「结果」。
 *
 * ⚠️ 两处必须用**同一个函数**判定：结果栏显示哪些产出、那颗「清空」清哪些，
 * 各写一份谓词的话，必然出现「清不干净」或「清掉了没显示的」。
 */
export const isResultJob = (j: Job) => !j.remote && j.outputs.length > 0

/** 读回收起的名单；坏 JSON 就当没收起过（不让这一栏卡死） */
function loadClearedResults(): string[] {
  try {
    const raw = localStorage.getItem(RESULT_CLEARED_KEY)
    const list = raw ? (JSON.parse(raw) as unknown) : []
    return Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string') : []
  } catch {
    return []
  }
}

/**
 * 「还在跑」的判定 —— **唯一真源，必须导出复用**。
 *
 * 这个条件曾被手写复制到 5 个地方（4 个生成页的「本页历史」+ 顶栏的最近记录），
 * 写法都是 `j.state !== 'queued' && j.state !== 'running'`。
 * 现在消费方是 `StudioPanel` 与 `TopBar`，两边都从这里取。
 *
 * ⚠️ **再出现手写的 `state !== 'queued' && state !== 'running'` 就是 bug**：
 * 一旦 JobState 新增中间态（`cancelling` 之类），散落的副本会静默漏改，
 * 表象是「刚取消的任务还赖在历史里」。
 */
export const isActive = (s: JobState) => s === 'queued' || s === 'running'

/**
 * 任务列表的**规范顺序**：新的在前。
 *
 * 这一行原来抄了三处（合并服务端历史、合并后重排、`outputsFor` 取产出顺序），
 * 撤回清理失败时还要用第四处 —— 同一份规则四处维护，改一处必漏三处，
 * 表现是「某个视图里任务顺序不对」这种极难归因的毛病。收成一份。
 */
const byNewest = (a: Job, b: Job) => b.createdAt - a.createdAt

/**
 * 从本地缓存恢复任务列表。
 *
 * **活跃任务也要恢复**：后端调度器不依赖浏览器，刷新页面时任务还在跑，
 * 界面却把它抹掉的话，用户会以为任务丢了、然后重复提交。
 * 没有 serverJobId 的活跃任务恢复不了（拿不到后端 jobId，查不回来），直接丢掉。
 *
 * 只恢复**本机提交过**的任务；服务端历史由 `listJobs` 拉回来（见 remote 字段的说明）。
 *
 * ⚠️ 这里**不再过滤「已移除」的记录** —— 那份名单已经在后端了，
 * 接口回来的列表本来就只含未清理的，本机这份里也不可能混进服务端记录。
 */
function loadHistory(): Job[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY)
    const list = raw ? (JSON.parse(raw) as Job[]) : []
    return list.filter((j) => !isActive(j.state) || j.serverJobId).slice(0, 60)
  } catch {
    return []
  }
}

/**
 * 把老版本的浏览器黑名单（`hw_job_hidden_v1`）搬进库，然后删掉本地键。
 *
 * **只在迁移这一刻读它**。不做这一步的话，用户升级前清理过的记录会集体「复活」
 * —— 看起来像「清空没生效」，比不迁移更让人不信任。
 *
 * 只搬形如 `job_xxx` 的服务端 id：老名单里混着没落库的本地 id（提交就失败的任务），
 * 后端对未知 id 是静默忽略的，不搬它们只是少一次无用请求。
 * 读失败 / 后端不可达时**保留本地键**，下次启动再试 —— 搬一半就删会真的丢状态。
 */
async function migrateLegacyHidden(): Promise<void> {
  let ids: string[] = []
  try {
    const raw = localStorage.getItem(LEGACY_HIDDEN_KEY)
    if (!raw) return
    ids = (JSON.parse(raw) as string[]).filter((id) => typeof id === 'string' && id.startsWith('job'))
  } catch {
    // 坏 JSON：删掉这个键（它已经不可能再被解释），不给下次启动留一颗雷
    localStorage.removeItem(LEGACY_HIDDEN_KEY)
    return
  }
  if (!ids.length) {
    localStorage.removeItem(LEGACY_HIDDEN_KEY)
    return
  }
  try {
    await api.hideJobs(ids)
    localStorage.removeItem(LEGACY_HIDDEN_KEY)
  } catch {
    /* 后端不通：留着键，下次启动再搬 */
  }
}

/** 后端任务行 → 前端 Job（落进同一个列表，靠 `remote` 区分来源） */
function serverToJob(it: ServerJobItem): Job {
  return {
    id: `srv-${it.jobId}`,
    serverJobId: it.jobId,
    title: it.title || `${it.provider || it.appSlug} 任务`,
    appSlug: it.appSlug,
    sourcePage: it.sourcePage || it.appSlug,
    model: it.model,
    modelName: it.provider || undefined,
    prompt: it.prompt,
    // 逐张尺寸在后端是随 inputs 落库的（见 routers/aigc.py），这里读回来，
    // 后端历史任务的「重试」才不会把 A+ 各模块压回同一个比例
    ratios: Array.isArray((it.inputs as Record<string, unknown> | undefined)?.ratios)
      ? ((it.inputs as Record<string, unknown>).ratios as string[])
      : undefined,
    inputs: it.inputs ?? {},
    imageUrls: it.imageUrls ?? [],
    aspectRatio: it.aspectRatio || '1:1',
    resolution: it.resolution || undefined,
    expectedOutputs: it.expectedOutputs || it.outputs.length || 1,
    estimatedMs: it.estimatedMs || 45_000,
    state: it.state,
    progress: isActive(it.state) ? 0 : 100,
    outputs: it.outputs ?? [],
    error: it.error ?? undefined,
    coins: it.usage?.consumeCoins,
    cost: it.usage?.thirdPartyConsumeMoney,
    platform: it.platform || undefined,
    // 空串归一成 undefined —— 空串当批次 id 会让所有单个任务聚成一批
    batchId: it.batchId || undefined,
    // 已清理标记原样带回来：`hiddenJobs` 那份列表靠它渲染「已清理」徽章，
    // 而主列表拿到的接口数据本来就不含已清理的（后端在 SQL 里就滤掉了）
    hidden: it.hidden === true,
    createdAt: Date.parse(it.createdAt) || Date.now(),
    finishedAt: it.finishedAt ? Date.parse(it.finishedAt) : undefined,
    remote: true,
  }
}

export function JobCenterProvider({ children }: { children: ReactNode }) {
  const [jobs, setJobs] = useState<Job[]>(() => loadHistory())
  const jobsRef = useRef(jobs)
  jobsRef.current = jobs
  const inflight = useRef<Set<string>>(new Set())
  const cancelled = useRef<Set<string>>(new Set())
  /** 已清理的记录（库里的 `hidden=1`）：只在任务中心「已清理」视图里露面 */
  const [hiddenJobs, setHiddenJobs] = useState<Job[]>([])
  /** 库里已清理的总数，由后端下发（与分页无关） */
  const [hiddenCount, setHiddenCount] = useState(0)
  /**
   * 「结果」栏被收起过的任务 id（视图状态，见 `RESULT_CLEARED_KEY` 的注释）。
   *
   * 与 `hiddenJobs`/`hiddenCount` **不是一回事**：那份是库里的记录可见性，
   * 这份只是右栏第一张卡先不显示本轮产出。
   */
  const [clearedResults, setClearedResults] = useState<string[]>(loadClearedResults)
  const clearedResultSet = useMemo(() => new Set(clearedResults), [clearedResults])
  const [historyLoaded, setHistoryLoaded] = useState(false)
  const [historyError, setHistoryError] = useState<string | null>(null)
  const { addMany } = useWorks()
  const { addGenerated } = useAssetLibrary()
  const toast = useToast()
  // 额度是会员体系（2026-10-07）加的：跑完一个任务余额就变了，
  // 不主动拉一次的话，顶栏那个数字会**一直停在进站那一刻**，
  // 用户看到的是「任务花完钱但额度没动」，第一反应是扣费坏了。
  const { refresh: refreshBalance } = useAuth()
  // 提交时要把「模型族 id」翻成真实路由 slug，见下方 submit
  const { all } = useModels()

  useEffect(() => {
    // 只缓存**本机提交**的任务；服务端历史数据本来就在库里，存一份到浏览器
    // 既撑配额，又会在下次启动时和接口回来那份打架
    try {
      localStorage.setItem(
        HISTORY_KEY,
        JSON.stringify(jobs.filter((j) => !j.remote).slice(0, 60)),
      )
    } catch {
      /* ignore */
    }
  }, [jobs])

  // 收起名单落浏览器：刷新后结果栏不该自己又冒出来（那会被当成「清空没生效」）
  useEffect(() => {
    try {
      localStorage.setItem(RESULT_CLEARED_KEY, JSON.stringify(clearedResults))
    } catch {
      /* ignore */
    }
  }, [clearedResults])

  /**
   * 音频产出**自动进资产库**（2026-10-09 用户口径：「音频还是要存入资产库，
   * 到时候可以让数字人引用」）。
   *
   * ## 为什么放在这里，而不是 TtsStudio 页内
   *
   * 语音合成有**两个发起页**（`/tts` 与数字人页的「生成配音」），以后还会有第三个。
   * 在页面里各写一份，就是「同一份规则两处各写」—— 迟早出现「这页存了、那页没存」，
   * 而症状是「资产库里怎么没有刚才那段」。任务队列是唯一知道
   * 「所有音频产出」的地方，规则就放在这里。
   *
   * ## 三条判据都不是随手写的
   *
   *   · **`!j.remote`**：只登记本机提交的任务。远端记录是「库里本来就有」的历史，
   *     一进站把几十条老音频倒进资产库，用户会发现资产库莫名其妙满了 ——
   *     而「引用的候选」本该是他自己刚做的那几条。
   *   · **`type === 'audio'`**：只做音频。图片/视频产出量级完全不同（一次十几张），
   *     自动入库会把资产库变成第二个「我的作品」。
   *   · **`audioSaved` 那份 id 名单**：这个 effect 挂在 `jobs` 上，
   *     而 `jobs` 每轮轮询都会换一个新数组 —— 没有它，每次轮询都会重发一遍
   *     `addAsset`（`addGenerated` 虽然按 id 去重了**列表**，接口调用还是会打出去）。
   *
   * id 必须**稳定**（`asset-audio-<jobId>-<序号>`）：刷新页面后从
   * `localStorage` 复原任务时还会命中同一个 id，去重才成立。后端那一侧
   * 是 `INSERT OR REPLACE`，同 id 覆盖，所以即便重放也不会多出一行。
   */
  const audioSaved = useRef<Set<string>>(new Set())
  useEffect(() => {
    const fresh: AssetItem[] = []
    for (const j of jobs) {
      if (j.remote) continue
      j.outputs.forEach((o, i) => {
        if (o.type !== 'audio' || !o.url) return
        const id = `asset-audio-${j.id}-${i}`
        if (audioSaved.current.has(id)) return
        audioSaved.current.add(id)
        fresh.push({
          id,
          url: o.url,
          thumbUrl: o.url,
          name: `${j.title}${j.outputs.length > 1 ? ` #${i + 1}` : ''}`,
          kind: 'audio',
          createdAt: new Date().toISOString(),
          source: 'generated',
        })
      })
    }
    fresh.forEach(addGenerated)
  }, [jobs, addGenerated])

  /**
   * 把后端任务历史合并进任务列表。
   *
   * 合并规则：**本地已认领的不重复添加**（同一个 `serverJobId` 只留本地那条，
   * 因为它带着实时进度、模型族 id 与展示名）。
   *
   * ⚠️ 这里**不再拿浏览器黑名单过滤**（老版本有一份 `hiddenRef`）：
   * 接口默认就只返回未清理的记录，客户端的第二份名单只会和它打架。
   */
  const mergeServerJobs = useCallback((items: ServerJobItem[]) => {
    setJobs((list) => {
      const known = new Set(list.map((j) => j.serverJobId).filter(Boolean) as string[])
      const add: Job[] = []
      for (const it of items) {
        if (!it?.jobId || known.has(it.jobId)) continue
        add.push(serverToJob(it))
      }
      if (!add.length) return list
      const merged = [...list, ...add].sort(byNewest)
      const locals = merged.filter((j) => !j.remote)
      // 只裁服务端历史：本机会话任务（含正在跑的）一条都不许因为数量被挤掉
      const remotes = merged.filter((j) => j.remote).slice(0, REMOTE_CAP)
      return [...locals, ...remotes].sort(byNewest)
    })
  }, [])

  /**
   * 读后端任务历史：任务中心要能看到**失败原因**，不能只靠 localStorage
   * （清缓存 / 换浏览器 / 超过 60 条之后，本地那份就没了）。
   *
   * 同时读「已清理」那一份：它是恢复入口的数据源，也是「已清理 N 条」的数字来源。
   * 两次请求并发（都是本机 SQLite，毫秒级），串行只会让「读取中」多停一拍。
   */
  const pullHistory = useCallback(async () => {
    try {
      const [visible, hidden] = await Promise.all([
        api.listJobs({ limit: REMOTE_CAP }),
        api.listJobs({ limit: REMOTE_CAP, hidden: 'only' }),
      ])
      mergeServerJobs(visible.items)
      setHiddenJobs(hidden.items.map(serverToJob))
      // 数字以后端为准：它数的是整张表，前端只拿得到一页
      setHiddenCount(Math.max(hidden.hiddenCount, hidden.items.length))
      setHistoryError(null)
    } catch (e) {
      // 后端不可达时不打扰用户（本地会话任务照常工作），但**必须记下这件事** ——
      // 界面靠它把「读取失败」和「真的没有记录」分开，否则又是一页会说谎的空态。
      console.warn('[jobs] 任务历史读取失败', e)
      setHistoryError(e instanceof Error ? e.message : '任务历史读取失败')
    } finally {
      setHistoryLoaded(true)
    }
  }, [mergeServerJobs])

  useEffect(() => {
    // 顺序要紧：先把老黑名单搬进库，再读历史 —— 反过来会让「迁移前已清理的记录」
    // 在首屏闪一下再消失（用户看到自己删过的东西回来了）
    void migrateLegacyHidden().finally(() => void pullHistory())
    // 5 分钟一轮：历史变化很慢，没必要跟着状态轮询一起高频打
    const timer = window.setInterval(() => void pullHistory(), 300_000)
    return () => window.clearInterval(timer)
  }, [pullHistory])

  const finish = useCallback(
    (jobId: string, patch: Partial<Job>) => {
      setJobs((list) => list.map((j) => (j.id === jobId ? { ...j, ...patch } : j)))
    },
    [],
  )

  /** 把成功的输出落成「作品」，供作品库/素材库复用 */
  const persistOutputs = useCallback(
    (job: Job, outputs: JobOutput[]) => {
      const items: WorkItem[] = outputs
        .filter((o) => o.type !== 'text' || o.text)
        .map((o, i) => ({
          id: `work-${job.id}-${i}`,
          title: outputs.length > 1 ? `${job.title} #${i + 1}` : job.title,
          workType: o.type,
          url: o.url,
          thumbUrl: o.thumbUrl ?? o.url,
          contentText: o.text,
          sourcePage: job.sourcePage,
          sourceModel: job.modelName ?? job.model,
          prompt: job.prompt,
          createdAt: new Date().toISOString(),
          archiveKey: `${job.id}-${i}`,
          // 任务 id：作品在「我的作品」里靠它聚成一个图集
          jobId: job.serverJobId,
        }))
      addMany(items)
      return items
    },
    [addMany],
  )

  /* ------------------------- 本地进度（零网络） -------------------------
   * 进度条不能等服务端告诉你「涨到多少」——后端只回状态，不回百分比。
   * 所以这里每秒按估算公式自己推，动画才平滑。
   */
  useEffect(() => {
    const timer = window.setInterval(() => {
      const now = Date.now()
      setJobs((list) =>
        list.map((j) => {
          if (!isActive(j.state)) return j
          const pct = progressFrom(now - j.createdAt, j.estimatedMs)
          if (pct <= j.progress) return j
          return {
            ...j,
            progress: pct,
            state: j.state === 'queued' && pct > 8 ? 'running' : j.state,
          }
        }),
      )
    }, PROGRESS_TICK_MS)
    return () => window.clearInterval(timer)
  }, [])

  /* --------------------------- 状态同步循环 ---------------------------
   * 节奏见 src/config/polling.ts：**一律 60s 一次**（新任务的第一次确认是 3s）。
   *
   * 循环里**没有任何截止时间** —— 任务跑多久就一直跟多久，绝不因为「等太久」
   * 单方面放弃。放弃/判死的权力只在后端调度器手里（`JOB_TIMEOUT_SEC`），
   * 前端提前收手会让用户看到一条永远转圈、但其实早就出片的卡片。
   *
   * 用「唤醒式 sleep」而不是 setInterval：新任务提交/任务收尾时能立刻插队确认，
   * 不用干等一个完整周期。
   */
  const wakeRef = useRef<(() => void) | null>(null)
  const syncedAtRef = useRef<Record<string, number>>({})

  const sleep = useCallback((ms: number) => {
    return new Promise<void>((resolve) => {
      const timer = window.setTimeout(resolve, ms)
      wakeRef.current = () => {
        window.clearTimeout(timer)
        resolve()
      }
    })
  }, [])

  useEffect(() => {
    let stopped = false

    const syncJob = async (job: Job) => {
      if (cancelled.current.has(job.id) || inflight.current.has(job.id)) return
      inflight.current.add(job.id)
      try {
        const res = await api.poll(job.appSlug, job.serverJobId!)
        syncedAtRef.current[job.id] = Date.now()
        if (isActive(res.state)) {
          finish(job.id, { state: res.state })
        } else if (res.state === 'succeeded' || res.state === 'partial') {
          finish(job.id, {
            state: res.state,
            progress: 100,
            outputs: res.outputs,
            coins: res.usage?.consumeCoins,
            cost: (res.usage?.consumeCoins ?? 0) / 10,
            finishedAt: Date.now(),
          })
          const current = jobsRef.current.find((j) => j.id === job.id)
          persistOutputs({ ...(current ?? job), outputs: res.outputs }, res.outputs)
          toast.success(`「${job.title}」已完成`)
          // 结算完成（预扣 → 按实际用量多退少补），余额在这一刻才是最终值
          void refreshBalance()
        } else {
          finish(job.id, {
            state: 'failed',
            error: res.error ?? { code: 'UNKNOWN', message: '生成失败' },
            finishedAt: Date.now(),
          })
          toast.error(`「${job.title}」失败：${res.error?.message ?? '未知原因'}`)
          // 失败会**退款**（判据是「上游有没有真的接下这个活」），余额同样要重拉
          void refreshBalance()
        }
      } catch (e) {
        // 网络异常不把任务判死：后端还在跑，下一轮照样能把结果取回来。
        // （上一版在这里直接判 failed，一次断网就让用户以为任务没了）
        //
        // ⚠️ 「请求超时：20s 内后端没有任何响应」（client.ts 的 TIMEOUT_MS）也走这条路。
        // 那是**单次 HTTP 请求**的传输层闸门，跟任务本身跑了多久毫无关系 ——
        // 这里只记一个时间戳让下一轮再来，`state` 一个字节都不改。
        // 前端从头到尾**没有**任何「任务等太久就判失败」的逻辑。
        console.warn('[jobs] 状态确认失败，稍后重试', e)
        syncedAtRef.current[job.id] = Date.now()
      } finally {
        inflight.current.delete(job.id)
      }
    }

    const loop = async () => {
      while (!stopped) {
        const active = jobsRef.current.filter((j) => isActive(j.state) && j.serverJobId)
        for (const job of active) {
          if (stopped) return
          await syncJob(job)
        }

        // 下一次醒来的时间。只有两种可能：还没确认过的按 FIRST_SYNC_MS 早点问
        // （余额/Key 这类即时失败要当场暴露），确认过的统一 STATE_POLL_MS。
        // 取 min 是因为这一觉睡完要把**所有**活跃任务都过一遍。
        const gap = active.length
          ? Math.min(
              ...active.map((j) => (syncedAtRef.current[j.id] ? STATE_POLL_MS : FIRST_SYNC_MS)),
            )
          : IDLE_POLL_MS
        await sleep(gap)
      }
    }

    void loop()
    return () => {
      stopped = true
      wakeRef.current?.()
    }
  }, [finish, persistOutputs, toast, sleep])

  /* ------------------------------ 提交 ------------------------------ */
  const submit = useCallback(
    async (args: SubmitArgs): Promise<string> => {
      const localId = `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`
      const estimatedMs =
        args.estimatedMs ??
        (args.appSlug.includes('video') ? ESTIMATED_MS.videoAudio3d : ESTIMATED_MS.image)

      // 表单里存的是**模型族 id**（如 gpt-image-2.5），提交前才转成真实路由 slug：
      // 同一个模型的文生图与图生图是两个 slug，由「表单里有没有参考图」决定。
      // 用户因此只需要选模型，不需要理解 slug 的存在。
      const family = familyById(all, args.model)
      // 「有没有参考素材」而不是「有没有参考图」：上游选文生/图生 slug 看的是
      // **素材**存不存在。只传参考视频（图生视频的常见用法）时若判 false，
      // 会落到文生 slug 上 —— 贰春那边两个 slug 相同所以侥幸无碍，
      // 但这是运气不是设计，换成区分文生/图生的上游就会静默走错分支。
      const hasRef = (args.imageUrls ?? []).length > 0 || (args.videoUrls ?? []).length > 0
      const modelSlug = family ? slugForFamily(family, hasRef) : args.model
      const modelName = family?.name ?? args.model

      const job: Job = {
        id: localId,
        title: args.title,
        appSlug: args.appSlug,
        sourcePage: args.sourcePage,
        model: modelSlug,
        modelName,
        familyId: family?.id,
        prompt: args.prompt,
        prompts: args.prompts,
        ratios: args.ratios,
        inputs: args.inputs,
        imageUrls: args.imageUrls ?? [],
        videoUrls: args.videoUrls ?? [],
        aspectRatio: args.aspectRatio ?? '1:1',
        resolution: args.resolution,
        expectedOutputs: args.outputs ?? 1,
        estimatedMs,
        state: 'queued',
        progress: 0,
        outputs: [],
        platform: args.platform,
        batchId: args.batchId,
        createdAt: Date.now(),
      }
      setJobs((list) => [job, ...list])

      try {
        const res = await api.run({
          appSlug: args.appSlug,
          model: modelSlug,
          inputs: args.inputs,
          imageUrls: args.imageUrls,
          videoUrls: args.videoUrls,
          prompt: args.prompt,
          prompts: args.prompts,
          ratios: args.ratios,
          aspectRatio: args.aspectRatio,
          resolution: args.resolution,
          outputs: args.outputs,
          estimatedMs,
          // sourcePage / title 一起带上：后端要靠它们分页面统计与命名，
          // 漏掉的话 source_page 会退化成 appSlug、标题变成提示词截断
          sourcePage: args.sourcePage,
          title: args.title,
          platform: args.platform,
          // 批次 id：**必须在这里显式转发**，前端 onBatch 收下了却不往后端发的话，
          // 界面上一批是聚成卡的、刷新之后就散成 N 张 —— 因为后端压根没存。
          batchId: args.batchId,
          // skills 同理：SubmitArgs 收下了但这里不转发，前端开关就成了摆设 ——
          // 界面显示「开启」、后端收到的仍是 false，产物没变还查不出原因。
          skills: args.skills,
        })

        if (res.state === 'succeeded' && res.outputs) {
          // 文案类同步返回
          finish(localId, {
            state: 'succeeded',
            progress: 100,
            outputs: res.outputs,
            serverJobId: res.job.jobId,
            finishedAt: Date.now(),
          })
          persistOutputs(job, res.outputs)
          return localId
        }
        if (res.state === 'failed') {
          finish(localId, {
            state: 'failed',
            error: res.error ?? { code: 'SUBMIT_FAILED', message: '提交失败' },
            finishedAt: Date.now(),
          })
          return localId
        }
        finish(localId, { serverJobId: res.job.jobId, state: res.state })
        toast.toast(`已提交「${args.title}」，预计 ${Math.round(estimatedMs / 1000)} 秒`)
        // 提交时后端已经**预扣**了本次报价的额度，所以这里立刻刷一次，
        // 用户马上能看到余额下降（收尾结算时再多退少补）
        void refreshBalance()
        // 叫醒同步循环：新任务不用干等一个轮询周期才第一次查状态
        wakeRef.current?.()
        return localId
      } catch (e) {
        finish(localId, {
          state: 'failed',
          error: { code: 'SUBMIT_FAILED', message: e instanceof Error ? e.message : '提交失败' },
          finishedAt: Date.now(),
        })
        toast.error(e instanceof Error ? e.message : '提交失败')
        // 额度不足（402 INSUFFICIENT_COINS）也走这条路 —— 重拉一次，
        // 顶栏能显示真实余额，用户才知道差多少，而不是只看到一句「额度不足」。
        void refreshBalance()
        return localId
      }
    },
    [finish, persistOutputs, toast, all],
  )

  /**
   * 批量提交：一行产品一个任务，共用同一个批次 id。
   *
   * ⚠️ **生成批次 id 的地方只有这一处**。放到调用方（生成页）的话，生成页、
   * 报价弹窗、右栏聚合三处都得各自拿到它 —— 又变成「同一份东西两处维护」，
   * 而且一旦有一处漏传，表现是「提交成功但右栏不聚合」，不报错。
   */
  const submitBatch = useCallback(
    async (items: SubmitArgs[]): Promise<string[]> => {
      if (!items.length) return []
      const batchId = `batch-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`
      const ids: string[] = []
      for (const item of items) {
        // 串行，不是 Promise.all：上游网关对并发提交有限流，N 行一起打过去容易被
        // 判成刷单；串行还天然给出了「第几行」的顺序。
        //
        // 串行只串**提交**这一步（后端立即返回 jobId，出图在后台），所以不会
        // 把 20 行的出图时间串起来。
        //
        // `submit` 内部已经把错误吃进 `state: 'failed'` 并返回本地 id（不抛），
        // 所以某一行失败**不会中断**后面的行 —— 这就是「失败不连坐」。
        ids.push(await submit({ ...item, batchId }))
      }
      return ids
    },
    [submit],
  )

  /**
   * 恢复的**唯一实现**：把服务端那批行撤下 `hidden` 标记，然后重读历史。
   *
   * 收的是 **serverJobId**（不是本地 `Job.id`）—— 两个调用方手上拿的东西不同：
   *   · 任务中心的「恢复记录」拿的是列表里那条记录的 `Job.id`，得先反查；
   *   · 清理提示里的「撤销」拿的是**刚刚被移走那一批**的 serverJobId。
   *
   * ⚠️ 为什么「撤销」不能复用 `restoreJobs`：它从 `hiddenJobs` 里反查 serverJobId，
   * 而 `hiddenJobs` 是 `pullHistory` 异步拉回来的 —— 点下「清空」的那一刻它还是
   * **空的**，撤销会静默变成 no-op（「点了没反应」比没有这颗按钮更坏）。
   * 所以把网络这一段单独提出来，两个入口共用它，谁也不许各写一份。
   */
  const unhideOnServer = useCallback(
    async (serverJobIds: string[]): Promise<number> => {
      if (!serverJobIds.length) return 0
      /*
       * 恢复失败**必须说出来**：此时界面上那条还在「已清理」里，
       * 静默 `return 0` 会让人以为「点了没反应 = 这条记录不存在」。
       * 库里那一行一直没动，所以不重读也不会显示错 —— 说明白就够了。
       */
      let n = 0
      try {
        n = await api.unhideJobs(serverJobIds)
      } catch {
        toast.error('恢复没有保存到服务器 —— 记录仍在「已清理」里，稍后再试')
        return 0
      }
      // 重读：恢复出来的那条要连状态一起回来（它可能是 failed，也可能还在跑 ——
      // 恢复只改可见性，不改任务本身），「已清理」那一份与计数也跟着变
      void pullHistory()
      return n
    },
    [pullHistory, toast],
  )

  /**
   * 「清理」一批记录（任务卡上的「移除记录」/ 任务中心的「清空已结束」）
   * —— **记录级清理的唯一入口**。
   *
   * ⚠️ **右栏结果卡那颗「清空」不走这里**（它走 `clearResults`，只收起视图）：
   * 两个动作以前共用这一份实现，于是用户在结果卡上点「清空」，历史记录也一起
   * 进了「已清理」（2026-09-30 报障原话：「为什么点击清空了就没了，我明明清空是结果」）。
   * 判断某个入口该不该接这里，只看一件事：**用户是不是在动「记录」**。
   *
   * 三条必须守住的事：
   *
   * 1. **不是删除**。落库只置 `hidden`，行留在库里：任务的账（消耗、失败原因、
   *    原参数）就是拿来复盘与「按原参数重试」的。所以界面上的动作叫「清理」、
   *    不叫「删除」，恢复入口在任务中心的「已清理」里。
   * 2. **状态落库，不落浏览器**。老版本把「删过哪些」写进 localStorage，
   *    清缓存后视图状态没了、记录集体复活 —— 用户因此认为数据只在前端
   *    （2026-09-29 报障原话：「这些在前端那边…不然清空了就没」）。
   * 3. **先动界面再落库**。点一下就该消失，不等网络；后端不通时也只是
   *    「这次清理没生效」（下次读历史会看到它还在），而不是数据丢失。
   *
   * 收的是 **Job.id 列表**（不是页面键、也不是 serverJobId）：调用方手上只有
   * 正在显示的那批 job，键漂移在结构上不可能发生（同 `clearResults` 的理由）。
   */
  const hideRecords = useCallback(
    (ids: string[]) => {
      if (!ids.length) return
      const drop = new Set(ids)
      // 这批行要留着 —— 落库失败时得原样塞回去（见下面的 catch）
      const removed = jobsRef.current.filter((j) => drop.has(j.id))
      const serverIds = removed.map((j) => j.serverJobId).filter((id): id is string => !!id)
      setJobs((list) => list.filter((j) => !drop.has(j.id)))
      // 纯本地任务（提交那一刻就失败的）库里没有行，无事可做 —— 但要**说一声**：
      // 这类记录撤不回来（库里没有行可撤），静默消失会让人以为它进了「已清理」，
      // 去那里翻半天找不到。
      if (!serverIds.length) {
        toast.toast(`已清理 ${removed.length} 条本机记录 —— 它们没有提交到服务器，无法恢复`)
        return
      }
      // 落库后重读一次：库里的「已清理」那一份与计数要跟着变，
      // 否则任务中心会在用户刚清完的时候还显示旧数字
      void api
        .hideJobs(serverIds)
        .then((n) => {
          void pullHistory()
          /*
           * 清理**成功**也要说话，而且要说在点上。
           *
           * 用户 2026-09-30 的话：「以后也不要因为清空而失去，我们从数据库拿」。
           * 库里的行确实一行没少，但界面给出的观感只有「记录不见了」——
           * 不把这句话说出来，用户只能自己猜「是不是被删了」，然后又要翻库问一次。
           * 所以这里一次给两件事：
           *   1. 说明：记录没有被删除，在「任务中心 → 已清理」里可以恢复；
           *   2. 出口：一颗「撤销」，当场撤回这次清理（不必去翻列表）。
           *
           * ⚠️「撤销」走 `unhideOnServer(serverIds)`（见上面那段注释），
           *    不能走 `restoreJobs` —— 它依赖还没拉回来的 `hiddenJobs`。
           */
          const localOnly = removed.length - serverIds.length
          if (localOnly > 0)
            // 混着本机任务时**不给撤销**：撤回来一半，剩下的还晾着，
            // 用户会以为「撤销坏了」。说清楚哪部分撤得回来才是诚实的。
            toast.toast(`已清理 ${removed.length} 条 · 其中 ${localOnly} 条没有提交到服务器，无法恢复`, 'success')
          else if (n > 0)
            toast.toast(`已清理 ${n} 条 · 记录没有被删除，「任务中心 → 已清理」里随时可恢复`, 'success', {
              label: '撤销',
              onClick: () => void unhideOnServer(serverIds),
            })
        })
        .catch(() => {
          /*
           * 落库失败 —— 上面那句 `setJobs` 是**乐观更新**，必须当场撤回。
           *
           * ⚠️ 这里**不能只靠 `pullHistory()` 重读来撤回**：后端不通时重读也一样失败，
           * 记录会一直「消失」到联网为止 —— 界面说一套、库里另一套，正是用户那句
           * 「清空了就没 / 又回来了」的来源之一。所以把这批行按**规范顺序**
           * （`byNewest`，与合并服务端历史同一个规则）原样塞回去，再异步重读正规化。
           *
           * 文案也要准：此刻说的是「记录还在」，不是「已恢复」—— 后者在离线时会变成假话。
           */
          setJobs((list) => [...list, ...removed].sort(byNewest))
          void pullHistory()
          toast.error('清理没有保存到服务器 —— 这条记录还在，稍后再试')
        })
    },
    [pullHistory, toast, unhideOnServer],
  )

  const dismiss = useCallback((jobId: string) => hideRecords([jobId]), [hideRecords])

  const retry = useCallback(
    async (jobId: string) => {
      const job = jobsRef.current.find((j) => j.id === jobId)
      if (!job) return null
      cancelled.current.delete(jobId)
      setJobs((list) => list.filter((j) => j.id !== jobId))
      return submit({
        appSlug: job.appSlug,
        title: job.title,
        sourcePage: job.sourcePage,
        // 带 familyId 走正常解析；老记录只有 slug 时原样透传（familyById 查不到即不转换）
        model: job.familyId ?? job.model,
        prompt: job.prompt,
        prompts: job.prompts,
        ratios: job.ratios,
        inputs: job.inputs,
        imageUrls: job.imageUrls,
        videoUrls: job.videoUrls,
        aspectRatio: job.aspectRatio,
        // 分辨率与投放平台必须原样带回：缺了它们，重试出来的图和原任务
        // 不是一个规格（默认档 / 中文），而用户以为只是「再试一次」
        resolution: job.resolution,
        platform: job.platform,
        // 批次归属原样带回：重试的是**这一行**，它仍属于原来那一批。
        // 丢了的话，重试成功的那一行会从批次卡里「单飞」出去变成一张独立的卡 ——
        // 用户看到的是「重试之后那一行不见了，旁边多出来一个任务」。
        batchId: job.batchId,
        outputs: job.expectedOutputs,
        estimatedMs: job.estimatedMs,
        // skills 从 inputs 里恢复（后端落库时写在 inputs.skills）：
        // 原任务开了技能加载器，重试就得用同一套跑法 —— 否则同一份输入两次产出
        // 完全不同，用户只会以为「这个开关时灵时不灵」。
        skills: job.inputs?.skills === true,
      })
    },
    [submit],
  )

  const cancel = useCallback((jobId: string) => {
    cancelled.current.add(jobId)
    const job = jobsRef.current.find((j) => j.id === jobId)
    setJobs((list) =>
      list.map((j) =>
        j.id === jobId && isActive(j.state)
          ? { ...j, state: 'failed', error: { code: 'ABORTED', message: '已取消' }, finishedAt: Date.now() }
          : j,
      ),
    )
    // 后端也得知道：不然调度器会一直查到超时，白烧上游配额
    if (job?.serverJobId) void api.cancel(job.appSlug, job.serverJobId)
    wakeRef.current?.()
  }, [])

  const clearFinished = useCallback(() => {
    hideRecords(jobsRef.current.filter((j) => !isActive(j.state)).map((j) => j.id))
  }, [hideRecords])

  /**
   * 按 id 批量收起「结果」栏 —— 生成页结果卡那颗「清空」用。
   *
   * **不是清理记录**（改动前后各是什么，见接口上那段注释与 `isResultJob`）：
   * 这里只把产出从**本机这一栏**收起来，历史卡里的记录一条不动 ——
   * 记录该不该消失，是任务卡上「移除记录」/ 任务中心「清空已结束」的事。
   */
  const clearResults = useCallback(
    (ids: string[]) => {
      if (!ids.length) return
      const drop = new Set(ids)
      setClearedResults((list) => [...new Set([...list, ...ids])].slice(-RESULT_CLEARED_CAP))
      /*
       * 收起**必须说话**，而且要说在点上。
       *
       * 上一轮的病根就是「动作没解释」：记录真的从列表里消失了，而唯一的说明是
       * 一句「记录没有被删除」—— 用户得先相信这句话，才敢继续用。现在这个动作
       * 根本不碰记录，那就直接把它说出来：**历史记录一条没动**。
       *
       * 也顺手给一颗「撤销」：收起是可逆的视图动作，和清理记录一样该当场能撤回
       * （复用同一颗提示动作按钮，不另起一套浮层）。
       */
      toast.toast('已清空结果区 · 历史记录一条没动（记录与产出都在，点开任意一条仍能看到）', 'success', {
        label: '撤销',
        onClick: () => setClearedResults((list) => list.filter((id) => !drop.has(id))),
      })
    },
    [toast],
  )

  /**
   * 恢复一批被清理的记录（任务中心「已清理」视图）。
   *
   * 库里那一行本来就在，恢复只是把 `hidden` 置回 0；随后重读历史，
   * 让这条记录带着它的产出与失败原因**原样回到主列表**（不重建、不重跑）。
   */
  const restoreJobs = useCallback(
    async (ids: string[]): Promise<number> => {
      if (!ids.length) return 0
      const drop = new Set(ids)
      // 先把本地 `Job.id` 反查成 serverJobId，网络那一段交给唯一实现 `unhideOnServer`
      const serverIds = hiddenJobs
        .filter((j) => drop.has(j.id))
        .map((j) => j.serverJobId)
        .filter((id): id is string => !!id)
      if (!serverIds.length) return 0
      const n = await unhideOnServer(serverIds)
      // 只在真的改动了库时才动这两份视图状态（失败时 n=0，列表原样留着，
      // 配合上面那句「记录仍在『已清理』里」）
      if (n > 0) {
        setHiddenJobs((list) => list.filter((j) => !drop.has(j.id)))
        setHiddenCount((c) => Math.max(0, c - n))
      }
      return n
    },
    [hiddenJobs, unhideOnServer],
  )

  const activeJobs = useMemo(() => jobs.filter((j) => isActive(j.state)), [jobs])

  /**
   * 「本页任务」。
   *
   * ⚠️ 两边都过 `canonPageKey`：页面合并过五次，后端里躺着 `ai-set` /
   * `detail-main` / `fashion-model-gen` 这些**旧页面键**写下的记录，
   * 而页面现在报的是新键。只折算一头，另一半照样查不出来 —— 而且
   * 查不出来是**静默**的：右栏不报错，历史就是空的，用户以为自己记错了。
   * 映射表在 `config/pageKeys.ts`（单一真源，页面不许自己再抄一份判断）。
   *
   * ⚠️ `appSlug`（可选）是**第二层收窄**，管的是另一件事：一个页面里有多个
   * 工作流时（`/professional/products` 有 8 档、详情页 5 档、图片工具有 7 个工具），
   * 它们的 `sourcePage` **相同**、`app_slug` **不同**。只按页面键过滤，
   * 右栏就会串档 —— 用户切到「场景图」，标题写着「场景图 · 结果」，
   * 里面却是上一档 AI 模特图的产出（2026-09-29 用户报障，附截图）。
   * 页面知道自己当前是哪一档，所以由**调用方**把它传下来。
   *
   * 不传 = 本页全量，即改动前的行为。键本来就按档派生的页面
   * （社媒图 `social-${channel}`、数字人）不需要传 ——
   * 它们一档一个页面键，天然不串，多传一个参数反而多一处要同步的键。
   * 视频合并页正好相反：它**一个页面键对应两个 app_slug**（并档前留下的
   * `main-video` 记录还在库里），所以那一页传的是**数组**，见页面上的
   * `STUDIO_VIDEO_SLUGS` 与判据 `slugHit()`。
   */
  const jobsFor = useCallback(
    (sourcePage: string, appSlug?: string | string[]) => {
      const key = canonPageKey(sourcePage)
      return jobs.filter(
        (j) => canonPageKey(j.sourcePage) === key && slugHit(j.appSlug, appSlug),
      )
    },
    [jobs],
  )

  const outputsFor = useCallback(
    (sourcePage: string, appSlug?: string | string[]) => {
      const key = canonPageKey(sourcePage)
      return (
        jobs
          // ⚠️ 这里**刻意排除 remote**：右栏「本轮结果」是本机本轮的实时面板。
          // 把库里的历史产出一起倒进来，标题里的「本轮」就成了假话，
          // 而且一进页面就铺几十张历史图。历史归任务中心 / 我的作品。
          //
          // 已收起的（结果卡上点过「清空」）也在这里滤掉 —— 判据 `isResultJob`
          // 与那颗按钮**共用**，两边不许各写一份（否则会出现「清不干净」）。
          .filter(
            (j) =>
              canonPageKey(j.sourcePage) === key &&
              isResultJob(j) &&
              !clearedResultSet.has(j.id) &&
              slugHit(j.appSlug, appSlug),
          )
          .flatMap((j) => j.outputs)
          .slice(0, 60)
      )
    },
    [jobs, clearedResultSet],
  )

  /**
   * 候选参考图 —— 与 `outputsFor` 的差别**有两处，都是刻意的**：
   *   ① 保留 `remote`（见接口上的注释）；
   *   ② **不收「结果栏已收起」的影响**：收起只是把右栏那一栏先不显示，
   *      产出还在、记录还在 —— 上一轮跑出来的图照样得能挑来当参考图。
   *      哪天有人「顺手统一」成 `outputsFor`，这里就会少掉一批可选素材。
   * 另外按 `createdAt` 显式排序，不依赖 `jobs` 当前恰好是排好的：
   * 「最新那张默认被选中」是页面上的默认值，顺序错了默认值就错。
   */
  const refCandidatesFor = useCallback(
    (sourcePage: string, appSlug?: string | string[]) => {
      const key = canonPageKey(sourcePage)
      const ordered = jobs
        .filter(
          (j) =>
            canonPageKey(j.sourcePage) === key &&
            j.outputs.length &&
            slugHit(j.appSlug, appSlug),
        )
        .slice()
        .sort(byNewest)

      const seen = new Set<string>()
      const urls: string[] = []
      for (const job of ordered) {
        for (const o of job.outputs) {
          if (o.type !== 'image' || !o.url || seen.has(o.url)) continue
          seen.add(o.url)
          urls.push(o.url)
        }
      }
      return urls
    },
    [jobs],
  )

  const value = useMemo<JobCenterApi>(
    () => ({
      jobs,
      activeJobs,
      activeCount: activeJobs.length,
      submit,
      submitBatch,
      retry,
      cancel,
      dismiss,
      clearFinished,
      clearResults,
      clearedResultIds: clearedResultSet,
      jobsFor,
      outputsFor,
      refCandidatesFor,
      historyLoaded,
      historyError,
      hiddenJobs,
      hiddenCount,
      restoreJobs,
      refreshHistory: pullHistory,
    }),
    [
      jobs,
      activeJobs,
      submit,
      submitBatch,
      retry,
      cancel,
      dismiss,
      clearFinished,
      clearResults,
      clearedResultSet,
      jobsFor,
      outputsFor,
      refCandidatesFor,
      historyLoaded,
      historyError,
      hiddenJobs,
      hiddenCount,
      restoreJobs,
      pullHistory,
    ],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useJobCenter() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useJobCenter must be used within JobCenterProvider')
  return ctx
}
